import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  Package,
  Store,
  Wallet,
  TrendingUp,
  ShoppingBag,
  Users,
  Copy,
  Check,
  Plus,
  Minus,
  MapPin,
  ShieldCheck,
  Eye,
  ArrowRight,
  Gift,
  ChevronRight,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

// ─── Skeleton primitives ───────────────────────────────────────────────────
const Sk = ({ className = "" }) => (
  <div
    className={`animate-pulse rounded bg-gray-200 ${className}`}
    style={{ animation: "pulse 1.5s cubic-bezier(0.4,0,0.6,1) infinite" }}
  />
);

function StatCardSkeleton() {
  return (
    <Card>
      <CardContent className="p-6">
        <div className="flex items-center justify-between">
          <div className="space-y-2 flex-1">
            <Sk className="h-3 w-24" />
            <Sk className="h-7 w-32" />
            <Sk className="h-3 w-20" />
          </div>
          <Sk className="w-12 h-12 rounded-xl" />
        </div>
      </CardContent>
    </Card>
  );
}

function DashboardSkeleton() {
  return (
    <div className="space-y-8">
      {/* Header skeleton */}
      <div className="space-y-2">
        <Sk className="h-8 w-56" />
        <Sk className="h-4 w-40" />
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
        {[...Array(5)].map((_, i) => <StatCardSkeleton key={i} />)}
      </div>

      {/* Body */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Card><CardContent className="p-6 space-y-4">
            <Sk className="h-5 w-32" />
            <div className="grid grid-cols-4 gap-3">
              {[...Array(4)].map((_, i) => <Sk key={i} className="h-20 rounded-xl" />)}
            </div>
          </CardContent></Card>
          <Card><CardContent className="p-6 space-y-4">
            <Sk className="h-5 w-36" />
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[...Array(4)].map((_, i) => <Sk key={i} className="h-24 rounded-xl" />)}
            </div>
          </CardContent></Card>
        </div>
        <Card><CardContent className="p-6 space-y-4">
          <Sk className="h-5 w-28" />
          <Sk className="h-20 rounded-lg" />
          <Sk className="h-12 rounded-xl" />
          <Sk className="h-10 rounded-lg" />
        </CardContent></Card>
      </div>
    </div>
  );
}

// ─── Main component ────────────────────────────────────────────────────────
export default function DashboardPage() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  const [withdrawalOpen, setWithdrawalOpen] = useState(false);
  const [withdrawalForm, setWithdrawalForm] = useState({
    amount: "",
    method: "moncash",
    account_number: "",
    local_bank: "",
    account_holder: "",
    local_account_number: "",
    intl_beneficiary: "",
    intl_bank_name: "",
    intl_iban: "",
    intl_swift: "",
    intl_country: "",
    intl_currency: "USD",
  });
  const [withdrawalFees, setWithdrawalFees] = useState({
    moncash:    { fee_htg: 0,    label: "Gratuit" },
    natcash:    { fee_htg: 0,    label: "Gratuit" },
    bank_local: { fee_htg: 650,  label: "5 USD (≈ 650 HTG)" },
    bank_intl:  { fee_htg: 6500, label: "50 USD (≈ 6500 HTG)" },
  });
  const [submittingWithdrawal, setSubmittingWithdrawal] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [transactions, setTransactions] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  useEffect(() => {
    fetchStats();
    // Charger les frais de retrait depuis l'API
    axios.get(`${API_URL}/wallet/withdrawal-fees`)
      .then(r => setWithdrawalFees(r.data))
      .catch(() => {});
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API_URL}/dashboard/stats`);
      setStats(response.data);
    } catch (error) {
      console.error("Error fetching stats:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchHistory = async () => {
    setLoadingHistory(true);
    try {
      const response = await axios.get(`${API_URL}/dashboard/wallet/history`);
      setTransactions(response.data);
      setHistoryOpen(true);
    } catch (error) {
      toast.error("Impossible de charger l'historique");
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleWithdraw = async () => {
    if (!withdrawalForm.amount || parseFloat(withdrawalForm.amount) <= 0) {
      toast.error("Veuillez entrer un montant valide.");
      return;
    }
    setSubmittingWithdrawal(true);
    try {
      const payload = { amount: parseFloat(withdrawalForm.amount), method: withdrawalForm.method, details: {} };
      if (withdrawalForm.method === 'moncash' || withdrawalForm.method === 'natcash') {
        payload.details = { account_number: withdrawalForm.account_number };
      } else if (withdrawalForm.method === 'bank_local') {
        payload.details = { bank: withdrawalForm.local_bank, account_number: withdrawalForm.local_account_number, account_holder: withdrawalForm.account_holder };
      } else if (withdrawalForm.method === 'bank_intl') {
        payload.details = { beneficiary: withdrawalForm.intl_beneficiary, bank_name: withdrawalForm.intl_bank_name, iban: withdrawalForm.intl_iban, swift: withdrawalForm.intl_swift, country: withdrawalForm.intl_country, currency: withdrawalForm.intl_currency };
      }
      await axios.post(`${API_URL}/wallet/withdraw`, payload);
      toast.success("Demande de retrait envoyée ! Traitement sous 24-48h.");
      setWithdrawalOpen(false);
      fetchStats();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de la demande");
    } finally {
      setSubmittingWithdrawal(false);
    }
  };

  const copyReferralCode = () => {
    if (user?.referral_code) {
      navigator.clipboard.writeText(user.referral_code);
      setCopied(true);
      toast.success("Code copié !");
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const roleConfig = {
    CLIENT: { label: "Client", desc: "Acheteur", icon: ShoppingBag, color: "bg-blue-500", bg: "bg-blue-50", text: "text-blue-700" },
    SELLER: { label: "Vendeur", desc: "Boutique", icon: Store, color: "bg-green-500", bg: "bg-green-50", text: "text-green-700" },
    SUPPLIER: { label: "Fournisseur", desc: "Grossiste", icon: Package, color: "bg-orange-500", bg: "bg-orange-50", text: "text-orange-700" },
    ARTISAN: { label: "Artisan", desc: "Créateur", icon: TrendingUp, color: "bg-purple-500", bg: "bg-purple-50", text: "text-purple-700" },
    ADMIN: { label: "Admin", desc: "Plateforme", icon: ShieldCheck, color: "bg-red-600", bg: "bg-red-50", text: "text-red-700" },
  };

  const activeRoles = user?.roles?.map((r) => r.role_type) || [];

  const quickActions = [
    { to: "/commandes", icon: Package, label: "Mes commandes", desc: "Suivre vos achats" },
    { to: "/mes-boutiques", icon: Store, label: "Mes boutiques", desc: "Gérer vos ventes" },
    { to: "/catalogue", icon: ShoppingBag, label: "Catalogue", desc: "Explorer les produits" },
    { to: "/profil", icon: MapPin, label: "Mes adresses", desc: "Gérer vos lieux de livraison" },
  ];

  return (
    <div className="min-h-screen bg-gray-50" data-testid="dashboard-page">
      <Navbar />

      <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl py-8">
        {loading ? (
          <DashboardSkeleton />
        ) : (
          <>
            {/* ── Header ─────────────────────────────────────────────── */}
            <div className="mb-8 flex items-start justify-between">
              <div>
                <h1 className="font-bold text-2xl md:text-3xl text-gray-900 mb-1">
                  Bonjour, {user?.first_name} 👋
                </h1>
                <p className="text-gray-500 text-sm">
                  Voici un résumé de votre activité sur BuyMore
                </p>
              </div>
              <div className="hidden md:flex items-center gap-2 bg-white border rounded-xl px-4 py-2 shadow-sm text-sm text-gray-600">
                <div className="w-2 h-2 rounded-full bg-green-500" />
                Compte actif
              </div>
            </div>

            {/* ── Stats Grid ─────────────────────────────────────────── */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 mb-8">

              {/* Commandes */}
              <Card className="border-0 shadow-sm hover:shadow-md transition-shadow">
                <CardContent className="p-5">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Commandes</p>
                      <p className="text-3xl font-black text-gray-900">{stats?.total_orders || 0}</p>
                      <p className="text-xs text-gray-400 mt-1">Total passées</p>
                    </div>
                    <div className="w-11 h-11 bg-blue-50 rounded-xl flex items-center justify-center shrink-0">
                      <Package className="h-5 w-5 text-blue-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Dépenses */}
              <Card className="border-0 shadow-sm hover:shadow-md transition-shadow">
                <CardContent className="p-5">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Dépensé</p>
                      <p className="text-2xl font-black text-gray-900">{(stats?.total_spent || 0).toFixed(0)}</p>
                      <p className="text-xs text-gray-400 mt-1">HTG payés</p>
                    </div>
                    <div className="w-11 h-11 bg-red-50 rounded-xl flex items-center justify-center shrink-0">
                      <ShoppingBag className="h-5 w-5 text-red-500" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Revenus vendeur */}
              <Card className="border-0 shadow-sm hover:shadow-md transition-shadow">
                <CardContent className="p-5">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Revenus</p>
                      <p className="text-2xl font-black text-gray-900">{(stats?.total_revenue || 0).toFixed(0)}</p>
                      <p className="text-xs text-gray-400 mt-1">HTG gagnés (ventes)</p>
                    </div>
                    <div className="w-11 h-11 bg-green-50 rounded-xl flex items-center justify-center shrink-0">
                      <TrendingUp className="h-5 w-5 text-green-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Commissions parrainage */}
              <Card className="border-0 shadow-sm hover:shadow-md transition-shadow">
                <CardContent className="p-5">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Commissions</p>
                      <p className="text-2xl font-black text-gray-900">{(stats?.total_commissions || 0).toFixed(0)}</p>
                      <p className="text-xs text-gray-400 mt-1">HTG (parrainage)</p>
                    </div>
                    <div className="w-11 h-11 bg-amber-50 rounded-xl flex items-center justify-center shrink-0">
                      <Users className="h-5 w-5 text-amber-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Portefeuille — carte spéciale */}
              <div
                className="rounded-2xl p-5 text-white cursor-pointer relative overflow-hidden shadow-md hover:shadow-lg transition-shadow"
                style={{ background: "linear-gradient(135deg, #7c3aed, #4f46e5)" }}
                onClick={fetchHistory}
              >
                {/* decorative circles */}
                <div className="absolute -top-4 -right-4 w-20 h-20 bg-white/10 rounded-full" />
                <div className="absolute -bottom-6 -left-4 w-24 h-24 bg-white/5 rounded-full" />

                <div className="relative">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-1.5 bg-white/20 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider">
                      <Eye className="h-3 w-3" /> Portefeuille
                    </div>
                    <Wallet className="h-5 w-5 opacity-60" />
                  </div>
                  <p className="text-3xl font-black tracking-tight">{(stats?.wallet_balance || 0).toFixed(2)}</p>
                  <p className="text-xs opacity-70 mt-0.5">HTG disponibles</p>
                  <button
                    className="mt-3 text-xs bg-white/20 hover:bg-white/30 text-white font-semibold px-3 py-1.5 rounded-lg transition-colors w-full text-center"
                    onClick={(e) => { e.stopPropagation(); setWithdrawalOpen(true); }}
                  >
                    Retirer mes fonds →
                  </button>
                </div>
              </div>
            </div>

            {/* ── Body ───────────────────────────────────────────────── */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2 space-y-6">

                {/* Actions rapides */}
                <Card className="border-0 shadow-sm">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base font-bold text-gray-800">Actions rapides</CardTitle>
                    <p className="text-xs text-gray-400">Accédez rapidement aux sections principales</p>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {quickActions.map((a) => {
                        const Icon = a.icon;
                        return (
                          <Link key={a.to} to={a.to}>
                            <div className="flex flex-col items-center text-center p-4 rounded-xl border border-gray-100 bg-gray-50 hover:bg-white hover:border-gray-200 hover:shadow-sm transition-all group cursor-pointer">
                              <div className="w-10 h-10 bg-white rounded-xl shadow-sm flex items-center justify-center mb-2 group-hover:shadow-md transition-shadow">
                                <Icon className="h-5 w-5 text-gray-700" />
                              </div>
                              <span className="text-xs font-semibold text-gray-800">{a.label}</span>
                              <span className="text-[10px] text-gray-400 mt-0.5">{a.desc}</span>
                            </div>
                          </Link>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>

                {/* Rôles actifs */}
                <Card className="border-0 shadow-sm">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base font-bold text-gray-800">Mes rôles actifs</CardTitle>
                    <p className="text-xs text-gray-400">Vos accès et fonctionnalités disponibles sur la plateforme</p>
                  </CardHeader>
                  <CardContent>
                    {activeRoles.length === 0 ? (
                      <div className="text-center py-6 text-gray-400 text-sm">Aucun rôle configuré.</div>
                    ) : (
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {Object.entries(roleConfig).map(([role, config]) => {
                          if (!activeRoles.includes(role)) return null;
                          const Icon = config.icon;
                          return (
                            <div key={role} className={`p-4 rounded-xl border ${config.bg} flex flex-col items-center text-center gap-2`}>
                              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${config.color}`}>
                                <Icon className="h-5 w-5 text-white" />
                              </div>
                              <span className={`font-bold text-sm ${config.text}`}>{config.label}</span>
                              <span className="text-[10px] text-gray-500">{config.desc}</span>
                              <Badge className="text-[10px] bg-white/70 text-gray-600 border-0 shadow-none">Actif</Badge>
                            </div>
                          );
                        })}
                        {activeRoles.length === 1 && activeRoles.includes('CLIENT') && (
                          <Link to="/mes-boutiques" className="col-span-3">
                            <div className="flex items-center gap-3 p-4 rounded-xl border border-dashed border-gray-200 hover:border-green-300 hover:bg-green-50 transition-all text-sm text-gray-500 hover:text-green-700 group">
                              <div className="w-10 h-10 rounded-xl border-2 border-dashed border-gray-200 group-hover:border-green-300 flex items-center justify-center">
                                <Plus className="h-4 w-4" />
                              </div>
                              <div>
                                <p className="font-semibold">Ouvrir une boutique</p>
                                <p className="text-xs text-gray-400">Commencez à vendre sur BuyMore</p>
                              </div>
                              <ChevronRight className="h-4 w-4 ml-auto opacity-40 group-hover:opacity-100" />
                            </div>
                          </Link>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* ── Parrainage ───────────────────────────────────────── */}
              <div className="space-y-6">
                <Card className="border-0 shadow-sm">
                  <CardHeader className="pb-3">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center">
                        <Gift className="h-4 w-4 text-amber-600" />
                      </div>
                      <div>
                        <CardTitle className="text-base font-bold text-gray-800">Parrainage</CardTitle>
                        <p className="text-xs text-gray-400">Gagnez en invitant des amis</p>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl p-4 border border-amber-100">
                      <p className="text-xs text-gray-600 mb-3 leading-relaxed">
                        Partagez votre code unique. Pour chaque achat effectué par la personne que vous invitez, vous gagnez une commission automatiquement.
                      </p>
                      <div className="flex items-center gap-2">
                        <code className="flex-1 bg-white border border-amber-200 px-3 py-2 rounded-lg font-mono text-base font-bold text-gray-800 text-center tracking-widest">
                          {user?.referral_code || "---"}
                        </code>
                        <button
                          onClick={copyReferralCode}
                          data-testid="copy-referral"
                          className="w-10 h-10 rounded-lg border border-amber-200 bg-white flex items-center justify-center hover:bg-amber-50 transition-colors"
                        >
                          {copied
                            ? <Check className="h-4 w-4 text-green-500" />
                            : <Copy className="h-4 w-4 text-gray-500" />}
                        </button>
                      </div>
                    </div>

                    {/* Niveaux de commission */}
                    <div className="space-y-2">
                      <p className="text-xs font-semibold text-gray-600 uppercase tracking-wider">Vos taux de commission</p>
                      <div className="flex gap-2">
                        <div className="flex-1 bg-blue-50 rounded-xl p-3 text-center border border-blue-100">
                          <p className="text-xl font-black text-blue-700">1.5%</p>
                          <p className="text-[10px] text-blue-500 font-medium mt-0.5">Niveau 1</p>
                          <p className="text-[10px] text-gray-400">Vos filleuls directs</p>
                        </div>
                        <div className="flex-1 bg-green-50 rounded-xl p-3 text-center border border-green-100">
                          <p className="text-xl font-black text-green-700">0.5%</p>
                          <p className="text-[10px] text-green-500 font-medium mt-0.5">Niveau 2</p>
                          <p className="text-[10px] text-gray-400">Filleuls de vos filleuls</p>
                        </div>
                      </div>
                    </div>

                    <Link to="/parrainage">
                      <button className="w-full flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl border border-gray-200 hover:border-amber-300 hover:bg-amber-50 text-sm font-semibold text-gray-700 hover:text-amber-700 transition-all">
                        Voir mes gains & filleuls <ArrowRight className="h-4 w-4" />
                      </button>
                    </Link>
                  </CardContent>
                </Card>

                {/* Info rapide plateforme */}
                <Card className="border-0 shadow-sm bg-gradient-to-br from-gray-900 to-gray-800 text-white">
                  <CardContent className="p-5 space-y-3">
                    <p className="font-bold text-sm">💡 Comment ça marche ?</p>
                    <div className="space-y-2 text-xs text-gray-300 leading-relaxed">
                      <div className="flex gap-2"><span className="w-4 h-4 rounded-full bg-blue-500 flex items-center justify-center text-[10px] font-bold shrink-0 mt-0.5">1</span><span>Commandez des produits dans les boutiques disponibles.</span></div>
                      <div className="flex gap-2"><span className="w-4 h-4 rounded-full bg-green-500 flex items-center justify-center text-[10px] font-bold shrink-0 mt-0.5">2</span><span>Le livreur apporte votre colis à l'adresse choisie.</span></div>
                      <div className="flex gap-2"><span className="w-4 h-4 rounded-full bg-purple-500 flex items-center justify-center text-[10px] font-bold shrink-0 mt-0.5">3</span><span>Parrainez des amis et gagnez des commissions sur leurs achats.</span></div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </>
        )}
      </div>

      {/* ── Withdrawal Dialog ───────────────────────────────────────────── */}
      <Dialog open={withdrawalOpen} onOpenChange={setWithdrawalOpen}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-lg">
              <Wallet className="h-5 w-5 text-green-600" />
              Retirer mes gains
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-5 py-2">
            {/* Solde */}
            <div className="relative overflow-hidden rounded-2xl p-5 text-white text-center shadow-md" style={{ background: "linear-gradient(135deg, #16a34a, #065f46)" }}>
              <div className="absolute -top-4 -right-4 w-20 h-20 bg-white/10 rounded-full" />
              <p className="text-sm opacity-80 mb-1">Solde disponible</p>
              <p className="text-4xl font-black tracking-tight">{(stats?.wallet_balance || 0).toFixed(2)} <span className="text-xl font-semibold opacity-70">HTG</span></p>
              <p className="text-xs opacity-60 mt-1">Fonds disponibles après confirmation de livraison.</p>
            </div>

            {/* Montant */}
            <div className="space-y-1.5">
              <Label className="font-semibold">Montant à retirer <span className="text-red-500">*</span></Label>
              <div className="relative">
                <Input type="number" min="1" max={stats?.wallet_balance || 0} value={withdrawalForm.amount}
                  onChange={e => setWithdrawalForm({ ...withdrawalForm, amount: e.target.value })}
                  placeholder="Ex: 5000" className="pr-14 text-lg font-semibold" />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm font-medium">HTG</span>
              </div>
              <p className="text-xs text-gray-500">Minimum: 500 HTG · Maximum: {(stats?.wallet_balance || 0).toFixed(2)} HTG</p>
            </div>

            {/* Méthode */}
            <div className="space-y-1.5">
              <Label className="font-semibold">Mode de retrait <span className="text-red-500">*</span></Label>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { value: 'moncash', label: 'MonCash', emoji: '📱', desc: 'Mobile instantané' },
                  { value: 'natcash', label: 'NatCash', emoji: '📲', desc: 'Mobile instantané' },
                  { value: 'bank_local', label: 'Banque Locale', emoji: '🏦', desc: 'Virement en Haïti' },
                  { value: 'bank_intl', label: 'International', emoji: '🌐', desc: 'Virement étranger' },
                ].map(opt => (
                  <button key={opt.value} type="button"
                    onClick={() => setWithdrawalForm({ ...withdrawalForm, method: opt.value })}
                    className={`flex flex-col items-start p-3 rounded-xl border-2 text-left transition-all ${withdrawalForm.method === opt.value ? 'border-green-500 bg-green-50' : 'border-gray-200 hover:border-gray-300 bg-white'
                      }`}>
                    <span className="text-xl mb-1">{opt.emoji}</span>
                    <span className="font-semibold text-sm text-gray-900">{opt.label}</span>
                    <span className="text-[10px] text-gray-500">{opt.desc}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Champs Mobile Money */}
            {(withdrawalForm.method === 'moncash' || withdrawalForm.method === 'natcash') && (
              <div className="space-y-3 p-4 bg-blue-50 rounded-xl border border-blue-100">
                <p className="text-xs text-blue-700 font-medium">📱 Entrez le numéro associé à votre compte {withdrawalForm.method === 'moncash' ? 'MonCash' : 'NatCash'}.</p>
                <div className="space-y-1.5">
                  <Label>Numéro de téléphone <span className="text-red-500">*</span></Label>
                  <Input value={withdrawalForm.account_number}
                    onChange={e => setWithdrawalForm({ ...withdrawalForm, account_number: e.target.value })}
                    placeholder="Ex: 50933334444" type="tel" />
                </div>
              </div>
            )}

            {/* Champs Banque Locale */}
            {withdrawalForm.method === 'bank_local' && (
              <div className="space-y-3 p-4 bg-amber-50 rounded-xl border border-amber-100">
                <p className="text-xs text-amber-700 font-medium">🏦 Virement bancaire local — traitement sous 24 à 72h ouvrées.</p>
                <div className="space-y-1.5">
                  <Label>Banque <span className="text-red-500">*</span></Label>
                  <Select onValueChange={v => setWithdrawalForm({ ...withdrawalForm, local_bank: v })} value={withdrawalForm.local_bank}>
                    <SelectTrigger><SelectValue placeholder="Choisir votre banque..." /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="BNC">BNC — Banque Nationale de Crédit</SelectItem>
                      <SelectItem value="BUH">BUH — Banque de l'Union Haïtienne</SelectItem>
                      <SelectItem value="SOGEBANK">Sogebank</SelectItem>
                      <SelectItem value="UNIBANK">Unibank</SelectItem>
                      <SelectItem value="CAPITAL_BANK">Capital Bank</SelectItem>
                      <SelectItem value="BPH">BPH — Banque Populaire Haïtienne</SelectItem>
                      <SelectItem value="CITIBANK">Citibank Haïti</SelectItem>
                      <SelectItem value="LAFIALA">Lafia-la (Microfinance)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Nom du titulaire du compte <span className="text-red-500">*</span></Label>
                  <Input value={withdrawalForm.account_holder}
                    onChange={e => setWithdrawalForm({ ...withdrawalForm, account_holder: e.target.value })}
                    placeholder="Ex: Jean Pierre" />
                </div>
                <div className="space-y-1.5">
                  <Label>Numéro de compte <span className="text-red-500">*</span></Label>
                  <Input value={withdrawalForm.local_account_number}
                    onChange={e => setWithdrawalForm({ ...withdrawalForm, local_account_number: e.target.value })}
                    placeholder="Ex: 1234-5678-9012" />
                </div>
              </div>
            )}

            {/* Champs International */}
            {withdrawalForm.method === 'bank_intl' && (
              <div className="space-y-3 p-4 bg-purple-50 rounded-xl border border-purple-100">
                <p className="text-xs text-purple-700 font-medium">🌐 Virement international — traitement sous 3 à 5 jours ouvrés. Des frais de conversion peuvent s'appliquer.</p>
                <div className="space-y-1.5">
                  <Label>Nom du bénéficiaire <span className="text-red-500">*</span></Label>
                  <Input value={withdrawalForm.intl_beneficiary}
                    onChange={e => setWithdrawalForm({ ...withdrawalForm, intl_beneficiary: e.target.value })}
                    placeholder="Ex: Jean-Pierre Dupont" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label>Pays <span className="text-red-500">*</span></Label>
                    <Input value={withdrawalForm.intl_country}
                      onChange={e => setWithdrawalForm({ ...withdrawalForm, intl_country: e.target.value })}
                      placeholder="Ex: USA, France" />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Devise <span className="text-red-500">*</span></Label>
                    <Select onValueChange={v => setWithdrawalForm({ ...withdrawalForm, intl_currency: v })} value={withdrawalForm.intl_currency}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="USD">🇺🇸 USD</SelectItem>
                        <SelectItem value="EUR">🇪🇺 EUR</SelectItem>
                        <SelectItem value="CAD">🇨🇦 CAD</SelectItem>
                        <SelectItem value="GBP">🇬🇧 GBP</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label>Nom de la banque <span className="text-red-500">*</span></Label>
                  <Input value={withdrawalForm.intl_bank_name}
                    onChange={e => setWithdrawalForm({ ...withdrawalForm, intl_bank_name: e.target.value })}
                    placeholder="Ex: Chase Bank, BNP Paribas" />
                </div>
                <div className="space-y-1.5">
                  <Label>IBAN / Numéro de compte</Label>
                  <Input value={withdrawalForm.intl_iban}
                    onChange={e => setWithdrawalForm({ ...withdrawalForm, intl_iban: e.target.value })}
                    placeholder="Ex: FR76 3000 6000 0112 3456 7890 189"
                    className="font-mono text-sm" />
                </div>
                <div className="space-y-1.5">
                  <Label>Code SWIFT / BIC <span className="text-red-500">*</span></Label>
                  <Input value={withdrawalForm.intl_swift}
                    onChange={e => setWithdrawalForm({ ...withdrawalForm, intl_swift: e.target.value.toUpperCase() })}
                    placeholder="Ex: BNPAFRPPXXX" className="font-mono text-sm uppercase" />
                  <p className="text-[10px] text-gray-500">Le code SWIFT identifie votre banque à l'international. Il figure sur votre relevé de compte.</p>
                </div>
              </div>
            )}

            {/* Récapitulatif frais */}
            {(() => {
              const feeInfo = withdrawalFees[withdrawalForm.method] || { fee_htg: 0, label: "Gratuit" };
              const amount  = parseFloat(withdrawalForm.amount) || 0;
              const total   = amount + feeInfo.fee_htg;
              return (
                <div className={`rounded-xl p-4 border text-sm space-y-1.5 ${feeInfo.fee_htg > 0 ? 'bg-amber-50 border-amber-200' : 'bg-green-50 border-green-200'}`}>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Montant demandé</span>
                    <span className="font-semibold">{amount > 0 ? `${amount.toFixed(2)} HTG` : "—"}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Frais de retrait</span>
                    <span className={`font-semibold ${feeInfo.fee_htg > 0 ? 'text-amber-700' : 'text-green-700'}`}>{feeInfo.label}</span>
                  </div>
                  {amount > 0 && (
                    <div className="flex justify-between border-t border-current/20 pt-1.5 mt-1.5">
                      <span className="font-bold text-gray-800">Total débité</span>
                      <span className="font-black text-gray-900">{total.toFixed(2)} HTG</span>
                    </div>
                  )}
                </div>
              );
            })()}

            <div className="text-xs text-gray-500 bg-gray-50 p-3 rounded-lg border">
              ℹ️ Les demandes sont traitées manuellement sous 24-48h. Vous serez notifié par email.
            </div>

            <button onClick={handleWithdraw} disabled={submittingWithdrawal || !withdrawalForm.amount}
              className="w-full py-3 px-6 rounded-xl font-bold text-white text-base transition-all duration-200 shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ background: "linear-gradient(135deg, #16a34a, #065f46)" }}>
              {submittingWithdrawal ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                  </svg>
                  Envoi en cours...
                </span>
              ) : '✅ Confirmer la demande de retrait'}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ── Wallet History Dialog ───────────────────────────────────────── */}
      <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[80vh] flex flex-col p-0">
          <DialogHeader className="p-6 pb-2 border-b">
            <DialogTitle className="flex items-center gap-2">
              <Wallet className="h-5 w-5 text-purple-600" />
              Historique du Portefeuille
            </DialogTitle>
            <p className="text-xs text-gray-400 mt-1">Chaque mouvement de votre solde est enregistré ici</p>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto px-6 pb-6 pt-4">
            {loadingHistory ? (
              <div className="space-y-3">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="flex items-center gap-3 p-3 rounded-xl border animate-pulse">
                    <Sk className="w-9 h-9 rounded-full shrink-0" />
                    <div className="flex-1 space-y-2">
                      <Sk className="h-3 w-32" />
                      <Sk className="h-2.5 w-24" />
                    </div>
                    <div className="space-y-1 text-right">
                      <Sk className="h-4 w-20" />
                      <Sk className="h-2.5 w-16" />
                    </div>
                  </div>
                ))}
              </div>
            ) : transactions.length === 0 ? (
              <div className="py-16 text-center">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Wallet className="h-8 w-8 text-gray-300" />
                </div>
                <p className="font-semibold text-gray-600">Aucune transaction</p>
                <p className="text-sm text-gray-400 mt-1">Vos mouvements de fonds apparaîtront ici.</p>
              </div>
            ) : (
              <div className="space-y-2">
                {transactions.map((tx) => {
                  const isCredit = tx.transaction_type === 'CREDIT';
                  return (
                    <div key={tx.id} className="flex items-start justify-between p-3 rounded-xl border bg-white hover:bg-gray-50 transition-colors gap-3">
                      <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 mt-0.5 ${isCredit ? 'bg-green-100' : 'bg-red-100'}`}>
                        {isCredit ? <Plus className="h-4 w-4 text-green-600" /> : <Minus className="h-4 w-4 text-red-600" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-gray-900 leading-tight">
                          {tx.reason_label || tx.note || tx.reason || "Transaction"}
                        </p>
                        {tx.note && tx.note !== tx.reason_label && (
                          <p className="text-xs text-gray-500 mt-0.5 truncate">{tx.note}</p>
                        )}
                        {tx.order_number && (
                          <p className="text-xs text-blue-600 mt-0.5">Commande #{tx.order_number}</p>
                        )}
                        <p className="text-[10px] text-gray-400 mt-1 uppercase tracking-wider">
                          {tx.created_at ? new Date(tx.created_at).toLocaleString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}
                        </p>
                      </div>
                      <div className="text-right shrink-0">
                        <p className={`font-bold text-base ${isCredit ? 'text-green-600' : 'text-red-600'}`}>
                          {isCredit ? '+' : '-'}{tx.amount.toFixed(2)} HTG
                        </p>
                        <p className="text-[10px] text-gray-400 mt-0.5">Avant: {(tx.balance_before || 0).toFixed(2)}</p>
                        <p className="text-[10px] text-gray-500 font-medium">Après: {(tx.balance_after || 0).toFixed(2)}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
}
